import useSmartQueryClient from "@/hooks/useSmartQueryClient";
import { formatPercentage, roundToDigits } from "@/pages/dashboard/utils/formatters";
import { DeleteOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import { Button, Form, Input, InputNumber, message, Segmented } from "antd";
import { useForm, useWatch } from "antd/es/form/Form";
import axios from "axios";
import { ReactNode, useState } from "react";
import RootModal from "../RootModal/RootModal";
import styles from "./BomCreator.module.scss";

export type BomCreatorMode = "absolute" | "percentage";

export interface BomCreatorForm {
  mass: number;
  materials?: {
    name: string;
    supplier: string;
    supplier_address: string;
    percentage: number;
    absolute: number;
  }[];
}

export default function BomCreator({
  productId,
  children,
}: {
  productId: number;
  children: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [form] = useForm<BomCreatorForm>();

  const materials = useWatch("materials", form);
  const mass = useWatch("mass", form);
  const [mode, setMode] = useState<BomCreatorMode>("absolute");

  const totalAbsoluteMass = (materials || []).reduce(
    (prev, current) => prev + (current.absolute || 0),
    0
  );

  const onClose = () => {
    setIsOpen(false);
  };

  const onOpen = () => {
    setIsOpen(true);
  };

  const onChangeMode = (mode: BomCreatorMode) => {
    if (mode === "absolute") {
      form.setFieldsValue({
        materials: materials?.map((material) => ({
          ...material,
          absolute: mass * (material.percentage / 100) || undefined,
        })),
      });
    } else {
      form.setFieldsValue({
        materials: materials?.map((material) => ({
          ...material,
          percentage: (material.absolute / totalAbsoluteMass) * 100 || undefined,
        })),
        mass: totalAbsoluteMass || undefined,
      });
    }

    setMode(mode);
  };

  const queryInvalidator = useSmartQueryClient();

  const mutation = useMutation({
    mutationFn: (content: any) => axios.post("/products/" + productId + "/bom_creator", content),
    onSuccess: () => {
      queryInvalidator.invalidateQueries(["products"]);
    },
  });

  const onSave = () => {
    form
      .validateFields()
      .then((value) => {
        const content = {
          materials: (value.materials || []).map((x) => ({
            name: x.name,
            supplier: x.supplier,
            supplier_address: x.supplier_address,
            quantity: mode === "absolute" ? x.absolute : (x.percentage / 100) * mass,
          })),
        };

        mutation
          .mutateAsync(content)
          .then(() => onClose())
          .catch(() => {
            message.error(
              "Something went wrong unexpectedly. Please tray again. If the problem persists, contact support."
            );
          });
      })
      .catch(() =>
        message.error(
          "Creating bill of materials failed. Please provide the required fields and try again."
        )
      );
  };

  return (
    <>
      <span onClick={onOpen}>{children}</span>
      <RootModal
        width={1250}
        onCancel={onClose}
        open={isOpen}
        okText="Save"
        okButtonProps={{ loading: mutation.isLoading }}
        onOk={onSave}
        title="Create a bill of materials"
      >
        <Form
          form={form}
          initialValues={{ materials: [{ name: "" }], mode: "absolute" }}
          className={styles.BomCreator}
          onKeyUp={(e) => {
            if (e.code === "Enter") onSave();
          }}
        >
          <div className={styles.header}>
            <div className={styles.mass}>
              {mode === "percentage" ? (
                <>
                  Total product mass:
                  <Form.Item
                    noStyle
                    label="Product mass"
                    name={["mass"]}
                    rules={[
                      {
                        required: true,
                        message: "Total product mass is required.",
                      },
                    ]}
                  >
                    <InputNumber
                      min={0}
                      controls={false}
                      style={{ width: "150px" }}
                      addonAfter="kg"
                    />
                  </Form.Item>
                </>
              ) : (
                <>Total product mass: {roundToDigits(totalAbsoluteMass, 0, 8)} kg</>
              )}
            </div>
            <div className={styles.switch}>
              <Segmented
                onChange={onChangeMode}
                options={[
                  { label: "Absolute", value: "absolute" },
                  { label: "Percentage", value: "percentage" },
                ]}
              />
            </div>
          </div>
          <div className={styles.materials}>
            <Form.List name="materials">
              {(fields, { add, remove }) => (
                <>
                  <div className={`${styles.material} ${styles.header}`}>
                    <div>Material *</div>
                    <div>Supplier</div>
                    <div>Supplier address</div>
                    <div>%</div>
                    <div>Mass</div>
                  </div>
                  {materials &&
                    fields.map((field, index) => (
                      <>
                        <div className={styles.material}>
                          <div>
                            <Form.Item
                              name={[field.name, "name"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Name can not be empty.",
                                },
                              ]}
                              noStyle
                            >
                              <Input style={{ width: "100%" }} />
                            </Form.Item>
                          </div>
                          <div>
                            <Form.Item
                              name={[field.name, "supplier"]}
                              noStyle
                            >
                              <Input style={{ width: "100%" }} />
                            </Form.Item>
                          </div>
                          <div>
                            <Form.Item
                              name={[field.name, "supplier_address"]}
                              noStyle
                            >
                              <Input style={{ width: "100%" }} />
                            </Form.Item>
                          </div>
                          <div>
                            {mode === "percentage" ? (
                              <Form.Item
                                name={[field.name, "percentage"]}
                                rules={[
                                  {
                                    required: true,
                                    message: "Percentage can not be empty.",
                                  },
                                ]}
                                noStyle
                              >
                                <InputNumber
                                  controls={false}
                                  addonAfter="%"
                                  style={{ width: "100%" }}
                                />
                              </Form.Item>
                            ) : (
                              formatPercentage((materials[index].absolute || 0) / totalAbsoluteMass)
                            )}
                          </div>
                          <div>
                            {mode === "absolute" ? (
                              <Form.Item
                                name={[field.name, "absolute"]}
                                rules={[
                                  {
                                    required: true,
                                    message: "Absolute can not be empty.",
                                  },
                                ]}
                                noStyle
                              >
                                <InputNumber
                                  controls={false}
                                  addonAfter="kg"
                                  style={{ width: "100%" }}
                                />
                              </Form.Item>
                            ) : (
                              roundToDigits(
                                ((materials[index].percentage || 0) / 100) * (mass || 0),
                                0,
                                8
                              ) + " kg"
                            )}
                          </div>
                          <div style={{ display: "flex", justifyContent: "right" }}>
                            <Button
                              onClick={() => remove(field.name)}
                              danger
                            >
                              <DeleteOutlined />
                            </Button>
                          </div>
                        </div>
                      </>
                    ))}
                  <div
                    className={styles.add}
                    onClick={add}
                  >
                    <PlusCircleOutlined /> Add material
                  </div>
                </>
              )}
            </Form.List>
          </div>
        </Form>
      </RootModal>
    </>
  );
}
